import { ref, onBeforeUnmount } from 'vue';

// Refetches while the tab is visible. A hidden tab shows nothing, so its requests
// only cost the server. The data of a hidden tab is as old as the pause, so the
// tab refetches at once when it comes back.
export const useLiveRefresh = (callback, interval = 60000) => {
  const timeoutId = ref(null);
  const isActive = ref(false);
  const isRefreshing = ref(false);

  const isHidden = () => document.visibilityState === 'hidden';

  // A slow refetch must not get a second one beside it: the tab that comes back
  // during a refetch already waits for that data.
  const refresh = async () => {
    if (isRefreshing.value) return;

    isRefreshing.value = true;
    try {
      await callback();
    } finally {
      isRefreshing.value = false;
    }
  };

  const clearTimer = () => {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
  };

  const scheduleNext = () => {
    clearTimer();
    timeoutId.value = setTimeout(async () => {
      timeoutId.value = null;
      await refresh();
      if (isActive.value && !isHidden()) scheduleNext();
    }, interval);
  };

  const onVisibilityChange = async () => {
    if (!isActive.value) return;
    clearTimer();
    if (isHidden()) return;

    await refresh();
    if (isActive.value && !isHidden()) scheduleNext();
  };

  const startRefetching = () => {
    isActive.value = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (isHidden()) return;

    scheduleNext();
  };

  const stopRefetching = () => {
    isActive.value = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    clearTimer();
  };

  onBeforeUnmount(() => {
    stopRefetching();
  });

  return {
    startRefetching,
    stopRefetching,
  };
};
